import clsx from 'clsx'
import type {
  ChangeEventHandler,
  Dispatch,
  FocusEventHandler,
  PropsWithChildren,
  ReactNode,
  Ref,
  SetStateAction,
} from 'react'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Autocomplete from '../Autocomplete/Autocomplete'
import Group from '../Autocomplete/Group'
import Button from '../Button/Button'
import { useInitialRender } from '../hooks/useInitialRender'
import { useSyncRefs } from '../hooks/useSyncRefs'
import IconButton from '../IconButton/IconButton'
import { ExpandMore } from '../icons'
import Close from '../icons/Close'
import Menu, { useMenu } from '../Menu/Menu'
import type SvgIcon from '../SvgIcon/SvgIcon'
import type { TextInputSize, TextInputVariant } from '../TextInput/styles'
import Typography from '../Typography/Typography'
import type { Actions, MultiSelectValue } from './Actions'
import { ActionTypes } from './Actions'

export { DisplayModes } from '../Autocomplete/Autocomplete'

interface MultiSelectProps {
  defaultDisplayValue?: string
  defaultValue?: MultiSelectValue
  dense?: boolean
  displayRef?: Ref<HTMLInputElement>
  disabled?: boolean
  endAdornment?: typeof SvgIcon | string
  error?: boolean
  helperText?: string
  inputRef?: Ref<HTMLInputElement>
  label: string
  loading?: boolean
  name?: string
  onBlur?: FocusEventHandler<HTMLInputElement>
  onChange: ChangeEventHandler<HTMLInputElement>
  onFocus?: FocusEventHandler<HTMLInputElement>
  onSelect: (value: MultiSelectValue) => unknown
  size?: TextInputSize
  startAdornment?: typeof SvgIcon | string
  total?: number
  variant?: TextInputVariant
}

type MultiSelectState = {
  actions: ReactNode
  handleClose: () => void
  selections: MultiSelectValue
  setDisplayedSuggestionsValues: Dispatch<SetStateAction<MultiSelectValue>>
}

type MultiSelectReducers = {
  [ActionType in ActionTypes]: (
    state: MultiSelectState,
    action: Actions,
  ) => MultiSelectState
}

type UseMultiSelectValues = [MultiSelectState, Dispatch<Actions>]

const MultiSelectContext = createContext<UseMultiSelectValues | null>(null)

const reducers: MultiSelectReducers = {
  [ActionTypes.SetActions]: (
    state,
    action: Extract<Actions, { type: ActionTypes.SetActions }>,
  ) => ({
    ...state,
    actions: action.actions,
  }),
  [ActionTypes.SelectOption]: (
    state,
    action: Extract<Actions, { type: ActionTypes.SelectOption }>,
  ) => {
    return {
      ...state,
      selections: { ...state.selections, ...action.value },
    }
  },
  [ActionTypes.UnselectOptions]: (
    state,
    action: Extract<Actions, { type: ActionTypes.UnselectOptions }>,
  ) => {
    const selections = { ...state.selections }

    action.keys.forEach((key) => {
      delete selections[key]
    })

    return {
      ...state,
      selections,
    }
  },
}

const runReducer = (
  state: MultiSelectState,
  action: Actions,
): MultiSelectState => reducers[action.type](state, action)

export const useMultiSelect = (): UseMultiSelectValues =>
  useContext(MultiSelectContext) as UseMultiSelectValues

const MAX_SELECTIONS_SHOWN = 5

function MultiSelect({
  children,
  defaultDisplayValue = '',
  defaultValue = {},
  dense = false,
  disabled = false,
  displayRef,
  endAdornment: endAdornmentProp,
  error = false,
  helperText,
  inputRef = null,
  label,
  loading = false,
  name,
  onBlur,
  onChange,
  onFocus,
  onSelect,
  size = 'medium',
  startAdornment,
  total = -1,
  variant = 'outlined',
}: PropsWithChildren<MultiSelectProps>): JSX.Element {
  const intl = useIntl()
  const initialRender = useInitialRender()
  const [menuState, menuDispatch] = useMenu() ?? []
  const inMenu = !!menuDispatch

  const endAdornment =
    endAdornmentProp === undefined && !inMenu ? ExpandMore : endAdornmentProp

  const [
    displayedSuggestionsValues,
    setDisplayedSuggestionsValues,
  ] = useState<MultiSelectValue>({})
  const [closing, setClosing] = useState(false)
  const [showAllSelections, setShowAllSelections] = useState(false)
  const searchRef = useRef(defaultDisplayValue)

  const [state, dispatch] = useReducer(runReducer, null, () => ({
    actions: null,
    selections: defaultValue, // the selections that will be selected onBlur/Close
    setDisplayedSuggestionsValues,
    handleClose: () => setClosing(true),
  }))
  const [selections, setSelections] = useReducer(
    (prevSelections) => {
      const newSelections = {} as MultiSelectValue
      let selectionChanged = false

      Object.keys(state.selections).forEach((key) => {
        if ((state.selections as MultiSelectValue)[key].checked) {
          const handleClear = () => {
            dispatch({
              type: ActionTypes.UnselectOptions,
              keys: [key],
            })
          }

          newSelections[key] = {
            checked: true,
            handleClear,
          }

          if (!prevSelections[key]?.checked) {
            selectionChanged = true
          }
        }
      })

      // Since setting selections triggers onSelect
      // we want to do it only when the content of selections changes
      if (
        selectionChanged ||
        Object.keys(prevSelections).length !== Object.keys(newSelections).length
      ) {
        return newSelections
      }

      return prevSelections
    },
    null,
    () => defaultValue as MultiSelectValue,
  )

  const getSelectionLabel = useCallback(
    (selections) => {
      const filtered = Object.keys(selections).filter(
        (key) => selections[key].checked,
      )

      switch (filtered.length) {
        case 0:
          return ''
        case 1:
          return selections[filtered[0]].textLabel ||
            typeof selections[filtered[0]].label === 'string'
            ? (selections[filtered[0]].label as string)
            : intl.formatMessage({ id: 'Ui.number_selected' }, { number: 1 })
        case total:
          return intl.formatMessage(
            { id: 'Autocomplete.all_number_selected' },
            { number: total },
          )
        default:
          return intl.formatMessage(
            { id: 'Ui.number_selected' },
            { number: filtered.length },
          )
      }
    },
    [intl, total],
  )

  const localDisplayRef = useRef<HTMLInputElement>(null)
  const syncDisplayRef = useSyncRefs(localDisplayRef, displayRef)

  // this useEffect triggers the onSelect on initial render to pass in the handleClear function to default values
  // this occurs even when the clearing functionality is not intended to be used so should be looked into in the future
  useEffect(() => {
    if (defaultValue) {
      dispatch({
        type: ActionTypes.SelectOption,
        value: defaultValue,
      })
    }
  }, [])

  useEffect(() => {
    if (closing && !initialRender) {
      setClosing(false)
      setSelections()
    }
    // We don't want to trigger on initial render or selections change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closing])

  useEffect(() => {
    if (!initialRender) {
      onSelect?.(selections)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections])

  const setDisplayValue = (selections: MultiSelectValue) => {
    const display = localDisplayRef?.current

    if (display) {
      display.value = getSelectionLabel(selections)
    }
  }

  useEffect(() => {
    if (inMenu && !initialRender && !menuState?.show) {
      setClosing(true)
    }
    // don't want to trigger when value changes, only when it is closed, so we don't pass it as a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inMenu, menuState?.show])

  useEffect(() => {
    if (
      !dense &&
      !inMenu &&
      Object.keys(selections).length < MAX_SELECTIONS_SHOWN
    ) {
      setShowAllSelections(false)
    }
  }, [dense, inMenu, selections])

  const renderedSelections = useMemo(() => {
    if (dense || inMenu) {
      return
    }

    const tempSelections = Object.keys(selections).filter(
      (key) => (selections as MultiSelectValue)[key].checked,
    )

    if (!tempSelections.length) {
      return
    }

    const unselect = (key?: string) => {
      if (!key) {
        dispatch({
          type: ActionTypes.UnselectOptions,
          keys: Object.keys(selections),
        })

        return
      }

      const value = { ...selections }
      delete value[key]

      dispatch({
        type: ActionTypes.UnselectOptions,
        keys: [key],
      })
    }

    const allText = intl.formatMessage(
      { id: 'Autocomplete.all_number' },
      { number: total },
    )

    const additionalText = intl.formatMessage(
      { id: 'Ui.show_more' },
      { count: tempSelections.length - (MAX_SELECTIONS_SHOWN - 1) },
    )

    return (
      <ul aria-label={label} className="w-full pt-4 pl-4 space-y-2">
        {tempSelections.length === total ? (
          <li
            aria-label={allText}
            className={clsx(
              'flex items-center justify-between cursor-default',
              disabled ? 'text-black-60' : 'text-black-90',
            )}
          >
            <Typography color="inherit" variant="body2">
              {allText}
            </Typography>
            <IconButton
              aria-label={intl.formatMessage({ id: 'Ui.remove' })}
              disabled={disabled}
              onClick={() => unselect()}
            >
              <Close />
            </IconButton>
          </li>
        ) : (
          <>
            {tempSelections
              .slice(
                0,
                tempSelections.length > MAX_SELECTIONS_SHOWN &&
                  !showAllSelections
                  ? MAX_SELECTIONS_SHOWN - 1
                  : undefined,
              )
              .map((key) => {
                const label =
                  state.selections[key].textLabel ||
                  typeof state.selections[key].label === 'string'
                    ? (state.selections[key].label as string)
                    : ''
                return (
                  <li
                    aria-label={label}
                    className={clsx(
                      'flex items-center justify-between cursor-default',
                      disabled ? 'text-black-60' : 'text-black-90',
                    )}
                    key={key}
                  >
                    <Typography color="inherit" variant="body2">
                      {state.selections[key].label}
                    </Typography>
                    <IconButton
                      aria-label={intl.formatMessage({ id: 'Ui.remove' })}
                      disabled={disabled}
                      onClick={() => unselect(key)}
                    >
                      <Close />
                    </IconButton>
                  </li>
                )
              })}
            {tempSelections.length > MAX_SELECTIONS_SHOWN &&
            !showAllSelections ? (
              <li aria-label={additionalText} className="-ml-4">
                <Button
                  color="primary"
                  onClick={() => setShowAllSelections(true)}
                  variant="text"
                >
                  {additionalText}
                </Button>
              </li>
            ) : null}
          </>
        )}
      </ul>
    )
    // New selections should be rendered only on blur, so we only want to do it when selections change
    // and ignore state.value changes.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections, showAllSelections])

  useEffect(
    () => {
      const selectAll = () => {
        const newValue: MultiSelectValue = {}

        Object.keys(displayedSuggestionsValues).forEach((key) => {
          newValue[key] = {
            ...displayedSuggestionsValues[key],
            checked: true,
          }
        })

        dispatch({
          type: ActionTypes.SelectOption,
          value: newValue,
        })
      }

      const clearAll = () => {
        dispatch({
          type: ActionTypes.UnselectOptions,
          keys: Object.keys(displayedSuggestionsValues),
        })
      }

      const checkedCount = Object.values(displayedSuggestionsValues).filter(
        (value) => value.checked,
      ).length
      const allChecked =
        checkedCount === Object.keys(displayedSuggestionsValues).length
      const allUnchecked = checkedCount === 0

      const actions = (
        <>
          {allChecked ? null : (
            <Menu.Item action onClick={selectAll}>
              <FormattedMessage id="Ui.select_all" />
            </Menu.Item>
          )}
          {allUnchecked ? null : (
            <Menu.Item action onClick={clearAll}>
              <FormattedMessage id="Ui.clear_all" />
            </Menu.Item>
          )}
        </>
      )

      dispatch({ type: ActionTypes.SetActions, actions })
    },
    // We only want to trigger when displayedSuggestionsValues or loading changes
    [displayedSuggestionsValues],
  )

  useEffect(() => {
    // if the state.selections changes and input is not focused, change the actual selections
    if (!initialRender && localDisplayRef.current !== document.activeElement) {
      setSelections()

      if (inMenu || dense) {
        setDisplayValue(state.selections)
      }
    }
  }, [state.selections])

  const handleFocus: FocusEventHandler<HTMLInputElement> = (event) => {
    const display = localDisplayRef?.current

    if ((dense || inMenu) && display) {
      display.value = searchRef.current
    }

    onFocus?.(event)
  }

  const handleBlur: FocusEventHandler<HTMLInputElement> = (event) => {
    searchRef.current = localDisplayRef.current?.value ?? ''

    if (inMenu) {
      setSelections()
    }

    if (dense || inMenu) {
      setDisplayValue(state.selections)
    }

    onBlur?.(event)
  }

  return (
    <MultiSelectContext.Provider value={[state, dispatch]}>
      <Autocomplete
        defaultDisplayValue={
          inMenu || dense
            ? getSelectionLabel(defaultValue)
            : defaultDisplayValue
        }
        disabled={disabled}
        displayRef={syncDisplayRef}
        endAdornment={endAdornment}
        error={error}
        helperText={helperText}
        inputRef={inputRef}
        label={label}
        loading={loading}
        name={name}
        onBlur={handleBlur}
        onChange={onChange}
        onFocus={handleFocus}
        select={dense}
        size={size}
        startAdornment={startAdornment}
        variant={variant}
      >
        {children}
      </Autocomplete>
      {renderedSelections}
    </MultiSelectContext.Provider>
  )
}

interface SuggestionProps {
  disabled?: boolean
  value: string
  displayValue?: string
}

function Suggestion({
  children,
  disabled = false,
  displayValue,
  value,
}: PropsWithChildren<SuggestionProps>): JSX.Element {
  const [state, dispatch] = useMultiSelect()
  const checked = state?.selections[value]?.checked || false

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    dispatch({
      type: ActionTypes.SelectOption,
      value: {
        [event.currentTarget.name]: {
          checked: event.currentTarget.checked,
          label: value ? displayValue || children || '' : '',
          textLabel: displayValue || '',
        },
      },
    })
  }

  useEffect(() => {
    state.setDisplayedSuggestionsValues((prev) => ({
      ...prev,
      [value]: {
        checked,
        label: value ? displayValue || children || '' : '',
      },
    }))

    return () => {
      state.setDisplayedSuggestionsValues((prev) => {
        const displayedSuggestions = { ...prev }
        delete displayedSuggestions[value]

        return displayedSuggestions
      })
    }
    // "state" is not needed to be added to the dependency array.
    // All necessary pieces of state were added
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    checked,
    children,
    displayValue,
    state.setDisplayedSuggestionsValues,
    value,
  ])

  const multiProps = {
    checkbox: true,
    defaultChecked: state.selections[value]?.checked,
    onChange: handleChange,
  }

  return (
    <Autocomplete.Suggestion
      disabled={disabled}
      displayValue={displayValue}
      value={value}
      {...multiProps}
    >
      {children}
    </Autocomplete.Suggestion>
  )
}

MultiSelect.Group = Group
MultiSelect.Suggestion = Suggestion

export default MultiSelect
